import React, { createContext, useContext, useEffect, useRef } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { PartnerConfig, getPartnerConfig } from '../config/partnerConfigs';

const PartnerConfigContext = createContext<PartnerConfig | null>(null);

export function usePartnerConfig(): PartnerConfig | null {
  return useContext(PartnerConfigContext);
}

type PartnerConfigProviderProps = {
  slug?: string | null;
  children: React.ReactNode;
};

export function PartnerConfigProvider({
  slug,
  children,
}: PartnerConfigProviderProps) {
  const config = getPartnerConfig(slug);
  const { i18n } = useTranslation();
  const loadedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!config) return;
    const lang = i18n.language;
    const cacheKey = `${lang}:${config.slug}`;
    if (loadedRef.current.has(cacheKey)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/locales/${lang}/${config.slug}.json`);
        if (!res.ok) throw new Error(`status ${res.status}`);
        const bundle = await res.json();
        if (cancelled) return;
        Object.entries(bundle).forEach(([namespace, overrides]) => {
          i18n.addResourceBundle(lang, namespace, overrides, true, true);
        });
        loadedRef.current.add(cacheKey);
        i18n.emit('languageChanged', lang);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[PartnerConfig] Failed to load partner bundle for ${config.slug}/${lang}`,
            err,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config, i18n.language]);

  return (
    <PartnerConfigContext.Provider value={config}>
      {config?.assets.favicon && (
        <Head>
          <link rel="icon" href={config.assets.favicon} />
        </Head>
      )}
      {config ? (
        <div className={config.theme.cssClass}>{children}</div>
      ) : (
        children
      )}
    </PartnerConfigContext.Provider>
  );
}
